import { z } from "zod";
import {
  API_LIMITS,
  appVersionSchema,
  contentVersionSchema,
  itemIdSchema,
  sceneIdSchema,
  taskIdSchema,
  timestampSchema,
  uuidSchema,
} from "./primitives";

export const learningSessionStatusSchema = z.enum(["active", "completed", "abandoned"]);

export const createLearningSessionRequestSchema = z
  .object({
    clientSessionId: uuidSchema,
    contentVersion: contentVersionSchema,
    appVersion: appVersionSchema,
    assignmentRevisionId: uuidSchema.optional(),
  })
  .strict();

export const learningSessionResponseSchema = z
  .object({
    sessionId: uuidSchema,
    clientSessionId: uuidSchema,
    participantId: uuidSchema,
    contentVersion: contentVersionSchema,
    appVersion: appVersionSchema,
    assignmentRevisionId: uuidSchema.optional(),
    status: learningSessionStatusSchema,
    startedAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .strict();

export const clientLearningEventTypeSchema = z.enum([
  "scene_entered",
  "content_viewed",
  "interaction_completed",
  "archive_opened",
  "archive_completed",
  "source_viewed",
  "narration_changed",
  "hint_used",
  "remediation_started",
  "remediation_completed",
  "report_viewed",
  "scene_exited",
  "scene_completed",
  "quality_changed",
  "performance_sample",
  "resource_failed",
  "webgl_fallback",
]);

type ClientLearningEventTypeValue = z.infer<typeof clientLearningEventTypeSchema>;

export const clientEventPayloadKeysByType = {
  scene_entered: ["entry", "resumed", "moduleId", "reviewState", "blueprintVersion"],
  content_viewed: [
    "contentId",
    "contentKind",
    "action",
    "order",
    "kind",
    "provenance",
    "claimId",
    "assetId",
  ],
  interaction_completed: [
    "interaction",
    "formal",
    "nonScored",
    "evidenceIndex",
    "evidenceCount",
    "interactionMode",
    "remediationId",
    "variantId",
    "attempt",
    "misconceptionCode",
    "responseType",
    "citationEntryCount",
    "reason",
    "mode",
  ],
  archive_opened: ["momentId", "order", "total"],
  archive_completed: ["momentId", "scope", "total"],
  source_viewed: ["sourceId", "momentId", "assistant", "grounded"],
  narration_changed: ["enabled", "language"],
  hint_used: ["hintId", "attempt"],
  remediation_started: ["remediationId", "strategy", "triggerAttempt", "variantId"],
  remediation_completed: ["remediationId", "variantId", "attempt", "misconceptionCode", "formal"],
  report_viewed: ["reportId"],
  scene_exited: ["completed", "blocked"],
  scene_completed: ["taskCount", "archiveCount", "nodeCount", "claimCount"],
  quality_changed: ["reducedMotion", "highContrast", "subtitles", "quality"],
  performance_sample: [
    "estimatedFps",
    "viewportWidth",
    "viewportHeight",
    "dpr",
    "online",
    "quality",
  ],
  resource_failed: ["resourceId", "reasonCode"],
  webgl_fallback: ["reasonCode", "mode"],
} as const satisfies Record<ClientLearningEventTypeValue, readonly string[]>;

export function isAllowedClientEventPayloadKey(type: string, key: string): boolean {
  const allowed: readonly string[] | undefined =
    clientEventPayloadKeysByType[type as ClientLearningEventTypeValue];
  return allowed?.includes(key) ?? false;
}

const telemetryCounterSchema = z.number().int().min(0).max(10_000);
const telemetryPositiveCounterSchema = z.number().int().min(1).max(10_000);
const telemetryActionCodeSchema = z
  .string()
  .max(64)
  .regex(/^[a-z]+(?:_[a-z]+){1,5}$/);
const telemetryContentIdSchema = z
  .string()
  .max(96)
  .regex(
    /^(?:T(?:0[1-9]|1[0-9])|M[1-6]S[1-4]_(?:N\d{2}|LECTURE|C\d{2}|GX\d{2}(?:_[A-Z0-9]+)*)|GX\d{2}_(?:ORIGINAL|LABEL|C\d{2}(?:_\d{2})?))$/,
  );
const telemetryClaimIdSchema = z
  .string()
  .max(96)
  .regex(/^M[1-6]S[1-4]_CLAIM_[A-Z][A-Z0-9_]{2,79}$/);
const telemetryAssetIdSchema = z
  .string()
  .max(96)
  .regex(/^(?:AST-M[1-6]S[1-4]-[A-Z][A-Z0-9-]{1,79}|M[1-6]S[1-4]_ASSET_[A-Z][A-Z0-9_]{1,79})$/);
const telemetrySourceIdSchema = z
  .string()
  .max(96)
  .regex(/^SRC_[A-Z][A-Z0-9_]{2,79}$/);
const telemetryRemediationIdSchema = z
  .string()
  .regex(/^RBP-T(?:0[1-9]|1[0-9])-(?:0[1-9]|[1-9][0-9])$/);
const telemetryVariantIdSchema = z.string().regex(/^V-T(?:0[1-9]|1[0-9])-(?:0[1-9]|[1-9][0-9])$/);
const telemetryMisconceptionCodeSchema = z
  .string()
  .max(64)
  .regex(/^T(?:0?[1-9]|1[0-9])_[A-Z][A-Z_]*(?:_[1-9])?$/);
export const ARCHIVE_MOMENT_IDS = [
  "lugou-bridge",
  "national-telegram",
  "joint-declaration",
  "army-reorganization",
  "unity-summary",
  "guangxi-student-army",
] as const;
const telemetryMomentIdSchema = z.enum(ARCHIVE_MOMENT_IDS);
const telemetryResourceIdSchema = z.union([
  telemetryContentIdSchema,
  telemetryAssetIdSchema,
  telemetrySourceIdSchema,
]);

const eventPayloadSchemasByType: Record<ClientLearningEventTypeValue, z.ZodType> = {
  scene_entered: z
    .object({
      entry: z.enum(["timeline", "module-map", "module_map"]).optional(),
      resumed: z.boolean().optional(),
      moduleId: z.enum(["M1", "M2", "M3", "M4", "M5", "M6"]).optional(),
      reviewState: z.enum(["seed", "pending_review", "approved", "blocked"]).optional(),
      blueprintVersion: z.literal("1.0.0").optional(),
    })
    .strict(),
  content_viewed: z
    .object({
      contentId: telemetryContentIdSchema.optional(),
      contentKind: z.literal("GX01_C04_card").optional(),
      action: z.literal("tab_opened").optional(),
      order: telemetryPositiveCounterSchema.optional(),
      kind: z.enum(["node", "formal_task", "local_extension_label"]).optional(),
      provenance: z.enum(["textbook", "local_extension", "runtime_sequence"]).optional(),
      claimId: z.union([telemetryClaimIdSchema, z.literal("")]).optional(),
      assetId: z.union([telemetryAssetIdSchema, z.literal("")]).optional(),
    })
    .strict(),
  interaction_completed: z
    .object({
      interaction: telemetryActionCodeSchema.optional(),
      formal: z.boolean().optional(),
      nonScored: z.boolean().optional(),
      evidenceIndex: telemetryCounterSchema.optional(),
      evidenceCount: telemetryPositiveCounterSchema.optional(),
      interactionMode: telemetryActionCodeSchema.optional(),
      remediationId: telemetryRemediationIdSchema.optional(),
      variantId: telemetryVariantIdSchema.optional(),
      attempt: telemetryPositiveCounterSchema.optional(),
      misconceptionCode: z.union([telemetryMisconceptionCodeSchema, z.literal("")]).optional(),
      responseType: z.enum(["single_choice", "drag_sort", "drag_match"]).optional(),
      citationEntryCount: telemetryCounterSchema.optional(),
      reason: z
        .enum([
          "empty_query",
          "query_too_long",
          "prompt_injection",
          "prohibited_request",
          "contains_personal_data",
          "out_of_scene_scope",
          "unapproved_content",
          "no_reliable_match",
          "unknown",
        ])
        .optional(),
      mode: z
        .enum(["offline_grounded_p0", "content_review", "direct_retry", "required_variant"])
        .optional(),
    })
    .strict(),
  archive_opened: z
    .object({
      momentId: telemetryMomentIdSchema.optional(),
      order: telemetryPositiveCounterSchema.optional(),
      total: telemetryPositiveCounterSchema.optional(),
    })
    .strict(),
  archive_completed: z
    .object({
      momentId: telemetryMomentIdSchema.optional(),
      scope: z.enum(["moment", "scene_sequence"]).optional(),
      total: telemetryPositiveCounterSchema.optional(),
    })
    .strict(),
  source_viewed: z
    .object({
      sourceId: telemetrySourceIdSchema.optional(),
      momentId: telemetryMomentIdSchema.optional(),
      assistant: z.boolean().optional(),
      grounded: z.boolean().optional(),
    })
    .strict(),
  narration_changed: z
    .object({
      enabled: z.boolean().optional(),
      language: z.enum(["zh", "zh-CN", "en", "en-US"]).optional(),
    })
    .strict(),
  hint_used: z
    .object({
      hintId: z
        .string()
        .regex(/^HINT-T(?:0[1-9]|1[0-9])(?:-(?:0[1-9]|[1-9][0-9]))?$/)
        .optional(),
      attempt: telemetryPositiveCounterSchema.optional(),
    })
    .strict(),
  remediation_started: z
    .object({
      remediationId: telemetryRemediationIdSchema.optional(),
      strategy: z
        .enum([
          "automatic-after-incorrect",
          "retry",
          "source-review",
          "content_review",
          "direct_retry",
          "required_variant",
        ])
        .optional(),
      triggerAttempt: telemetryPositiveCounterSchema.optional(),
      variantId: telemetryVariantIdSchema.optional(),
    })
    .strict(),
  remediation_completed: z
    .object({
      remediationId: telemetryRemediationIdSchema.optional(),
      variantId: telemetryVariantIdSchema.optional(),
      attempt: telemetryPositiveCounterSchema.optional(),
      misconceptionCode: z.union([telemetryMisconceptionCodeSchema, z.literal("")]).optional(),
      formal: z.boolean().optional(),
    })
    .strict(),
  report_viewed: z
    .object({
      reportId: z
        .string()
        .regex(/^M[1-6]S[1-4]-(?:summary|progress|completion)$/)
        .optional(),
    })
    .strict(),
  scene_exited: z
    .object({ completed: z.boolean().optional(), blocked: z.boolean().optional() })
    .strict(),
  scene_completed: z
    .object({
      taskCount: telemetryCounterSchema.optional(),
      archiveCount: telemetryCounterSchema.optional(),
      nodeCount: telemetryCounterSchema.optional(),
      claimCount: telemetryCounterSchema.optional(),
    })
    .strict(),
  quality_changed: z
    .object({
      reducedMotion: z.boolean().optional(),
      highContrast: z.boolean().optional(),
      subtitles: z.boolean().optional(),
      quality: z.enum(["standard", "reduced-motion", "low", "medium", "high", "auto"]).optional(),
    })
    .strict(),
  performance_sample: z
    .object({
      estimatedFps: z.number().finite().min(0).max(1_000).optional(),
      viewportWidth: z.number().int().min(1).max(32_768).optional(),
      viewportHeight: z.number().int().min(1).max(32_768).optional(),
      dpr: z.number().finite().min(0.25).max(16).optional(),
      online: z.boolean().optional(),
      quality: z.enum(["standard", "reduced-motion", "low", "medium", "high", "auto"]).optional(),
    })
    .strict(),
  resource_failed: z
    .object({
      resourceId: telemetryResourceIdSchema.optional(),
      reasonCode: z
        .string()
        .max(64)
        .regex(/^[A-Z][A-Z_]*(?:_[1-5][0-9]{2})?$/)
        .optional(),
    })
    .strict(),
  webgl_fallback: z
    .object({
      reasonCode: z
        .string()
        .max(64)
        .regex(/^[A-Z][A-Z_]*(?:_[1-5][0-9]{2})?$/)
        .optional(),
      mode: z.enum(["2d", "static", "css", "unsupported", "reduced"]).optional(),
    })
    .strict(),
};

const eventPayloadValueSchema = z.union([
  z
    .string()
    .max(API_LIMITS.eventPayloadString)
    .regex(/^(?:[A-Za-z0-9][A-Za-z0-9._:/-]*)?$/),
  z.number().finite().min(-1_000_000_000).max(1_000_000_000),
  z.boolean(),
  z.null(),
]);

export const eventPayloadSchema = z
  .record(
    z
      .string()
      .min(1)
      .max(API_LIMITS.eventPayloadKey)
      .regex(/^[A-Za-z][A-Za-z0-9._-]*$/),
    eventPayloadValueSchema,
  )
  .superRefine((payload, context) => {
    if (Object.keys(payload).length > API_LIMITS.eventPayloadEntries) {
      context.addIssue({
        code: "custom",
        message: `Event payload may contain at most ${API_LIMITS.eventPayloadEntries} entries.`,
      });
    }

    const forbiddenKeys = new Set([
      "classname",
      "correct",
      "correctoptionid",
      "correctorder",
      "correctpairs",
      "email",
      "enrollmentid",
      "major",
      "name",
      "participantid",
      "phone",
      "score",
      "studentid",
      "userid",
    ]);
    for (const key of Object.keys(payload)) {
      const normalizedKey = key.replace(/[._-]/g, "").toLowerCase();
      if (forbiddenKeys.has(normalizedKey)) {
        context.addIssue({
          code: "custom",
          path: [key],
          message: "Event payload must not contain identity or authoritative assessment fields.",
        });
      }
    }
  });

export const clientLearningEventSchema = z
  .object({
    eventId: uuidSchema,
    occurredAt: timestampSchema,
    type: clientLearningEventTypeSchema,
    sceneId: sceneIdSchema.optional(),
    taskId: taskIdSchema.optional(),
    durationMs: z.number().int().min(0).max(API_LIMITS.eventDurationMs).optional(),
    payload: eventPayloadSchema,
  })
  .strict()
  .superRefine((event, context) => {
    for (const key of Object.keys(event.payload)) {
      if (!isAllowedClientEventPayloadKey(event.type, key)) {
        context.addIssue({
          code: "custom",
          path: ["payload", key],
          message: "Event payload keys must be selected from the event-specific telemetry schema.",
        });
      }
    }

    const payloadResult = eventPayloadSchemasByType[event.type].safeParse(event.payload);
    if (!payloadResult.success) {
      for (const issue of payloadResult.error.issues) {
        context.addIssue({
          code: "custom",
          path: ["payload", ...issue.path],
          message: issue.message,
        });
      }
    }
  });

export const eventBatchRequestSchema = z
  .object({
    batchId: uuidSchema,
    contentVersion: contentVersionSchema,
    appVersion: appVersionSchema,
    events: z.array(clientLearningEventSchema).min(1).max(API_LIMITS.eventBatch),
  })
  .strict()
  .superRefine((batch, context) => {
    const eventIds = batch.events.map((event) => event.eventId);
    if (new Set(eventIds).size !== eventIds.length) {
      context.addIssue({
        code: "custom",
        path: ["events"],
        message: "Event IDs must be unique within a batch.",
      });
    }
  });

export const eventBatchResponseSchema = z
  .object({
    batchId: uuidSchema,
    status: z.enum(["accepted", "duplicate"]),
    acceptedEventIds: z.array(uuidSchema).max(API_LIMITS.eventBatch),
    duplicateEventIds: z.array(uuidSchema).max(API_LIMITS.eventBatch),
    receivedAt: timestampSchema,
  })
  .strict()
  .superRefine((response, context) => {
    const allIds = [...response.acceptedEventIds, ...response.duplicateEventIds];
    if (allIds.length > API_LIMITS.eventBatch || new Set(allIds).size !== allIds.length) {
      context.addIssue({
        code: "custom",
        message: "Event result IDs must be unique and remain within the batch limit.",
      });
    }
    if (allIds.length === 0) {
      context.addIssue({ code: "custom", message: "An event batch result cannot be empty." });
    }
    if (response.status === "duplicate" && response.acceptedEventIds.length > 0) {
      context.addIssue({
        code: "custom",
        path: ["acceptedEventIds"],
        message: "A duplicate batch cannot report newly accepted events.",
      });
    }
  });

export const singleChoiceResponseSchema = z
  .object({
    type: z.literal("single_choice"),
    optionId: itemIdSchema,
  })
  .strict();

export const dragSortResponseSchema = z
  .object({
    type: z.literal("drag_sort"),
    orderedIds: z.array(itemIdSchema).min(2).max(API_LIMITS.taskItems),
  })
  .strict()
  .superRefine((response, context) => {
    if (new Set(response.orderedIds).size !== response.orderedIds.length) {
      context.addIssue({
        code: "custom",
        path: ["orderedIds"],
        message: "Ordered item IDs must be unique.",
      });
    }
  });

export const dragMatchResponseSchema = z
  .object({
    type: z.literal("drag_match"),
    pairs: z.record(itemIdSchema, itemIdSchema),
  })
  .strict()
  .superRefine((response, context) => {
    const entries = Object.entries(response.pairs);
    const rightIds = entries.map(([, rightId]) => rightId);
    if (entries.length < 2 || entries.length > API_LIMITS.taskItems) {
      context.addIssue({
        code: "custom",
        path: ["pairs"],
        message: `Match responses require 2-${API_LIMITS.taskItems} pairs.`,
      });
    }
    if (new Set(rightIds).size !== rightIds.length) {
      context.addIssue({
        code: "custom",
        path: ["pairs"],
        message: "Matched right-side IDs must be unique.",
      });
    }
  });

export const taskResponseSchema = z.discriminatedUnion("type", [
  singleChoiceResponseSchema,
  dragSortResponseSchema,
  dragMatchResponseSchema,
]);

export const taskAttemptRequestSchema = z
  .object({
    clientAttemptId: uuidSchema,
    contentVersion: contentVersionSchema,
    taskId: taskIdSchema,
    assignmentRevisionId: uuidSchema.optional(),
    response: taskResponseSchema,
  })
  .strict();

export const evaluationDiagnosticSchema = z
  .object({
    code: z.literal("ORDER_ADJACENCY_INVERTED"),
    message: z.string().min(1).max(API_LIMITS.feedback),
    itemIds: z.tuple([itemIdSchema, itemIdSchema]),
  })
  .strict();

export const taskAttemptResponseSchema = z
  .object({
    attemptId: uuidSchema,
    clientAttemptId: uuidSchema,
    sessionId: uuidSchema,
    contentVersion: contentVersionSchema,
    taskId: taskIdSchema,
    attemptNo: z.number().int().min(1).max(10_000),
    valid: z.boolean(),
    correct: z.boolean(),
    score: z.number().min(0).max(1),
    awardedPoints: z.number().int().min(0).max(100).optional(),
    feedback: z.string().min(1).max(API_LIMITS.feedback),
    misconceptionCode: z.string().min(1).max(64).optional(),
    diagnostic: evaluationDiagnosticSchema.optional(),
    evaluatedAt: timestampSchema,
  })
  .strict()
  .superRefine((result, context) => {
    if ((!result.valid || !result.correct) && result.score !== 0) {
      context.addIssue({
        code: "custom",
        path: ["score"],
        message: "Invalid or incorrect attempts must have a score of zero.",
      });
    }
    if (result.correct && (!result.valid || result.score !== 1)) {
      context.addIssue({
        code: "custom",
        path: ["correct"],
        message: "A correct attempt must be valid and have a score of one.",
      });
    }
  });

export type LearningSessionStatus = z.infer<typeof learningSessionStatusSchema>;
export type CreateLearningSessionRequest = z.infer<typeof createLearningSessionRequestSchema>;
export type LearningSessionResponse = z.infer<typeof learningSessionResponseSchema>;
export type ClientLearningEventType = z.infer<typeof clientLearningEventTypeSchema>;
export type ClientLearningEvent = z.infer<typeof clientLearningEventSchema>;
export type EventBatchRequest = z.infer<typeof eventBatchRequestSchema>;
export type EventBatchResponse = z.infer<typeof eventBatchResponseSchema>;
export type SingleChoiceResponse = z.infer<typeof singleChoiceResponseSchema>;
export type DragSortResponse = z.infer<typeof dragSortResponseSchema>;
export type DragMatchResponse = z.infer<typeof dragMatchResponseSchema>;
export type TaskResponse = z.infer<typeof taskResponseSchema>;
export type TaskAttemptRequest = z.infer<typeof taskAttemptRequestSchema>;
export type TaskAttemptResponse = z.infer<typeof taskAttemptResponseSchema>;
